import { ChevronDown } from 'lucide-react';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { createUserPreferenceStore } from '../../../../lib/preferences';
import { useCurrentSession } from '../../../../stores/session';
import styles from './builder.module.css';

// JOB-13's scoring matrix explains itself (06-qol §7: help is inline, never a hover tip). The
// three steps can be hidden once learned, remembered per viewer; the key to the tick columns
// stays, because those columns are too narrow to carry their own explanation and a phone hides
// the header row altogether.

const COLLAPSED_KEY = 'jobs.matrix-guide.collapsed';

export function MatrixGuide(): ReactNode {
  const userId = useCurrentSession()?.id ?? 'anonymous';
  const store = useMemo(() => createUserPreferenceStore(userId), [userId]);
  const [isOpen, setIsOpen] = useState(() => store.get<unknown>(COLLAPSED_KEY, false) !== true);
  const titleId = useId();
  const regionId = useId();

  function toggle(): void {
    const next = !isOpen;
    setIsOpen(next);
    store.set(COLLAPSED_KEY, !next);
  }

  return (
    <section className={styles.guide} aria-labelledby={titleId}>
      <div className={styles.guideHead}>
        <h3 id={titleId} className={styles.guideTitle}>
          How the scoring matrix works
        </h3>
        <button type="button" className={styles.guideToggle} aria-expanded={isOpen} aria-controls={regionId} onClick={toggle}>
          {isOpen ? 'Hide' : 'Show'}
          <span className={styles.guideChevron} aria-hidden>
            <ChevronDown size={16} />
          </span>
        </button>
      </div>
      {isOpen && (
        <div id={regionId} className={styles.guideBody}>
          <ol className={styles.guideSteps}>
            <li>List the qualities you want to judge, for example Communication.</li>
            <li>Say what a 1, a 3 and a 5 look like, so two interviewers score the same way.</li>
            <li>
              Tick where each quality is used. HR rates the &ldquo;HR review&rdquo; ones while reviewing CVs. Each interview round&rsquo;s scorecard is made from the
              qualities ticked for it.
            </li>
          </ol>
          <p className={styles.help}>
            A round&rsquo;s result is the average of its scores, and every quality counts the same. It passes at the round&rsquo;s pass mark: 3 out of 5 unless you
            change it for that round.
          </p>
        </div>
      )}
    </section>
  );
}

interface MatrixKeyProps {
  readonly roundCount: number;
  /** Where rounds come from on this screen, said when there are none yet. */
  readonly noRoundsNote: string;
}

/** What the three kinds of tick column mean, visible at every width. */
export function MatrixKey({ roundCount, noRoundsNote }: MatrixKeyProps): ReactNode {
  const roundsTerm = roundCount > 1 ? `Round 1 to ${roundCount}` : 'Round 1';
  return (
    <dl className={styles.matrixKey} aria-label="What the tick columns mean">
      <div className={styles.matrixKeyItem}>
        <dt>Critical</dt>
        <dd>If its average is below 2, the candidate can&rsquo;t move on, even when the round passes. HR can override this with a reason.</dd>
      </div>
      <div className={styles.matrixKeyItem}>
        <dt>HR review</dt>
        <dd>HR gives these 1 to 5 stars while reviewing CVs.</dd>
      </div>
      <div className={styles.matrixKeyItem}>
        <dt>{roundCount > 0 ? roundsTerm : 'Round columns'}</dt>
        <dd>{roundCount > 0 ? 'On that round’s scorecard.' : noRoundsNote}</dd>
      </div>
    </dl>
  );
}
